import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Switch,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  AstroLineCategory,
  AstroLineLayerVisibility,
} from "../../../../models/interfaces";

interface AstroLineLayersControlProps {
  layerVisibility: AstroLineLayerVisibility;
  onLayerVisibilityChange: (category: AstroLineCategory, value: boolean) => void;
  showLabels: boolean;
  onShowLabelsChange: (value: boolean) => void;
}

interface LayerDefinition {
  category: AstroLineCategory;
  labelKey: string;
  captionKey: string;
  /** Stroke preview mirroring how the layer is actually drawn on the map. */
  stroke: string;
}

const LAYERS: LayerDefinition[] = [
  {
    category: "main",
    labelKey: "astroLayerMain",
    captionKey: "astroLayerMainDesc",
    stroke: "3px solid #E4E4EF",
  },
  {
    category: "harm",
    labelKey: "astroLayerHarmonious",
    captionKey: "astroLayerHarmoniousDesc",
    stroke: "2px dotted #8FD6A8",
  },
  {
    category: "disharm",
    labelKey: "astroLayerDisharmonious",
    captionKey: "astroLayerDisharmoniousDesc",
    stroke: "2px dotted #E79AA0",
  },
];

/** Layer switches, each carrying a preview of the stroke it controls. */
const AstroLineLayersControl = (props: AstroLineLayersControlProps) => {
  const { t } = useTranslation();
  const {
    layerVisibility,
    onLayerVisibilityChange,
    showLabels,
    onShowLabelsChange,
  } = props;

  return (
    <List disablePadding sx={{ display: "grid", gap: 0.75 }}>
      {LAYERS.map((layer) => {
        const isActive = layerVisibility[layer.category];
        return (
          <ListItem
            key={layer.category}
            disablePadding
            secondaryAction={
              <Switch
                edge="end"
                checked={isActive}
                inputProps={{ "aria-label": t(layer.labelKey) }}
                onChange={(event) =>
                  onLayerVisibilityChange(layer.category, event.target.checked)
                }
              />
            }
          >
            <ListItemButton
              selected={isActive}
              onClick={() => onLayerVisibilityChange(layer.category, !isActive)}
            >
              <Box
                sx={{
                  width: 24,
                  flexShrink: 0,
                  borderTop: layer.stroke,
                  opacity: isActive ? 1 : 0.35,
                  transition: "opacity 0.2s ease",
                }}
              />
              <ListItemText
                primary={t(layer.labelKey)}
                secondary={t(layer.captionKey)}
                sx={{ my: 0, pr: 4 }}
              />
            </ListItemButton>
          </ListItem>
        );
      })}

      <ListItem
        disablePadding
        secondaryAction={
          <Switch
            edge="end"
            checked={showLabels}
            inputProps={{ "aria-label": t("astroShowLineNames") }}
            onChange={(event) => onShowLabelsChange(event.target.checked)}
          />
        }
      >
        <ListItemButton
          selected={showLabels}
          onClick={() => onShowLabelsChange(!showLabels)}
        >
          <LabelOutlinedIcon
            sx={{
              fontSize: "18px",
              flexShrink: 0,
              color: showLabels ? "var(--astro-accent)" : "text.disabled",
              transition: "color 0.2s ease",
            }}
          />
          <Typography variant="subtitle2" sx={{ flex: 1, pr: 4 }}>
            {t("astroShowLineNames")}
          </Typography>
        </ListItemButton>
      </ListItem>
    </List>
  );
};

export default AstroLineLayersControl;
